import { _ } from "~/i18n";
import { icon, node } from "~/util/dom";
import { getImage80x80 } from "~/util/cdn";
import { getFolders } from "~/util/providers/mapybox";
import { store } from "~/state/store";
import createSDSIcon from "../icon";
import { MYMAPS_STAR } from "../sds-compatible-icons";

export type TMyMapsFoldersSelectedType = "places" | "different-folder" | "folder" | "move-folder";
type TMyMapsFoldersCreateType = TMyMapsFoldersSelectedType | "new-folder";

export interface IMyMapsFolderItem {
	id: string;
	parent_id: string;
	title: string;
	img?: string;
}

interface IFolder {
	id: string;
	title: string;
}

interface ICreateItem {
	title: string;
	svg?: string;
	sdsIcon?: any;
	img?: string;
	isSelected?: boolean;
	active?: boolean;
	moveFolder?: boolean;
	isTrackLink?: boolean;
}

function createItem({
	title,
	svg = "",
	sdsIcon = null,
	img = "",
	isSelected = false,
	active = false,
	moveFolder = false,
	isTrackLink = false,
}: ICreateItem) {
	const container = node("div", { className: `mymaps-folders__selectedItem${isSelected ? " selected" : ""}` });
	const titlePart = node("span", { className: `mymaps-folders__selectedItemTitlePart` });

	if (img) {
		const imgElem = new Image();

		imgElem.classList.add("mymaps-folders__selectedItemImg");
		imgElem.src = img;
		titlePart.append(imgElem);
	} else if (sdsIcon) {
		titlePart.append(createSDSIcon({ className: "mymaps-folders__selectedItemSvg", symbol: sdsIcon }));
	} else if (svg) {
		const svgElem = icon(svg);

		svgElem.classList.add("mymaps-folders__selectedItemSvg");
		titlePart.append(svgElem);
	}

	const titleElem = node("span", { className: "mymaps-folders__selectedItemTitle", textContent: title });

	if (active) {
		titleElem.classList.add("active");
	}

	titlePart.append(titleElem);
	container.append(titlePart);

	if (isSelected) {
		const svgElem = icon("dropdown");

		svgElem.classList.add("mymaps-folders__selectedItemDropdown");
		container.append(svgElem);
	}

	if (moveFolder) {
		const moveTitle = isTrackLink
			? _("mymap.alreadyConnected")
			: _("mymap.actualFolder");
		const actualFolderPart = node("span", { className: `mymaps-folders__actualFolderTitlePart`, textContent: moveTitle });

		container.append(actualFolderPart);
	}

	return container;
}

const FOLDER_PLACEHOLDER = "/img/header-placeholder.svg";

function createItemByType(type: TMyMapsFoldersCreateType, data: Partial<ICreateItem> = {}, opts: { isTrackLink?: boolean; } = {}) {
	switch (type) {
		case "different-folder":
			return createItem({
				title: _("note.chooseDifferentFolder"),
				...data,
			});

		case "folder":
			return createItem({
				title: "",
				...data,
				img: data.img
					? getImage80x80(data.img)
					: FOLDER_PLACEHOLDER,
			});

		case "move-folder":
			return createItem({
				title: "",
				moveFolder: true,
				isTrackLink: opts.isTrackLink,
				...data,
			});

		case "places":
			return createItem({
				title: _("detail.content.mymapsplace.saved2"),
				sdsIcon: MYMAPS_STAR,
				...data,
			});

		case "new-folder":
			return createItem({
				title: _("note.createNewFolder"),
				svg: "add-circle",
				active: true,
			});

		default:
	}

	return node("div");
}

interface IMyMapsFolders {
	title?: string;
	disabled?: boolean;
	folder?: IFolder;
	moveFolders?: Array<IFolder>;
	isTrackLink?: boolean;
	selectFirstFolder?: boolean;
	onSelect?: (type: TMyMapsFoldersSelectedType, data?: IMyMapsFolderItem) => void;
}

export default class MyMapsFolders {
	_opts: IMyMapsFolders;

	_dom: {
		[key: string]: HTMLElement;
	};

	_isOpen: boolean;

	_selectedType: TMyMapsFoldersSelectedType;

	_selectedId: string;

	_rootId: string;

	constructor(opts: IMyMapsFolders) {
		this._opts = {
			title: "",
			disabled: false,
			moveFolders: [],
			isTrackLink: false,
			selectFirstFolder: false,
			onSelect: () => {},
			...opts,
		};
		this._dom = {};
		this._isOpen = false;
		this._selectedType = "places";
		this._selectedId = "";
		this._rootId = store.getState().mymaps.rootId;

		this._build();
	}

	get container() {
		return this._dom.container;
	}

	set moveFolders(moveFolders: IMyMapsFolders["moveFolders"]) {
		this._opts.moveFolders = moveFolders;
	}

	_build() {
		const container = node("div", { className: `mymaps-folders` });
		const selectedLine = node("div", { className: `mymaps-folders__selectedLine` });
		const choices = node("ul", { className: `mymaps-folders__choices` });

		const foldersProm = getFolders();
		const { folder, title } = this._opts;
		const initSelected = !this._opts.disabled;

		if (this._opts.disabled) {
			container.classList.add("disabled");
		}

		if (title) {
			selectedLine.append(createItemByType("places", {
				title,
				isSelected: initSelected,
			}));
			this._selectedType = "different-folder";
		} else if (folder) {
			selectedLine.append(createItemByType("folder", {
				title: folder.title,
				isSelected: initSelected,
			}));

			this._selectedType = "folder";
			this._selectedId = folder.id;
		} else {
			selectedLine.append(createItemByType("places", {
				isSelected: initSelected,
			}));
		}

		// ulozene, mame slozky, vybereme prvni
		if (this._opts.selectFirstFolder) {
			foldersProm.then((folders: Array<IMyMapsFolderItem>) => {
				if (folders.length > 0) {
					this._selectItem("folder", folders[0]);
				}
			});
		}

		selectedLine.addEventListener("click", () => {
			if (this._opts.disabled) {
				return;
			}

			this._isOpen = !this._isOpen;

			if (this._isOpen) {
				choices.innerHTML = "";

				const loading = this._createType("folder", {
					id: "",
					/* eslint-disable-next-line */
					parent_id: "",
					title: `${_("loading")}...`,
				}, true);

				choices.append(loading);

				foldersProm.then((folders: Array<IMyMapsFolderItem>) => {
					const checkIds = [this._selectedId];

					loading.remove();

					if (!this._isOpen) {
						return;
					}

					if (this._opts.moveFolders.length > 0) {
						let hasRoot = false;

						for (const moveFolder of this._opts.moveFolders) {
							const isRoot = moveFolder.id === this._rootId;
							const folderData = folders.filter(item => item.id === moveFolder.id)[0];

							choices.append(this._createType("move-folder", {
								...folderData,
								id: moveFolder.id,
								title: isRoot
									? _("detail.content.mymapsplace.saved2")
									: moveFolder.title,
								...isRoot
									? {
										sdsIcon: MYMAPS_STAR,
									}
									: {
										img: folderData.img ? getImage80x80(folderData.img) : FOLDER_PLACEHOLDER,
									},
							}, true));
							checkIds.push(moveFolder.id);
							hasRoot = hasRoot || isRoot;
						}

						if (this._selectedType === "folder" && !hasRoot) {
							choices.append(this._createType("places"));
						}
					} else if (this._selectedType !== "places") {
						choices.append(this._createType("places"));
					}

					if (this._selectedType !== "different-folder") {
						choices.append(this._createType("different-folder"));
					}

					const filtered = folders.filter(item => !checkIds.includes(item.id));
					const fragment = document.createDocumentFragment();

					for (const item of filtered) {
						const itemCover = this._createType("folder", item);

						fragment.append(itemCover);
					}

					choices.append(fragment);
				}, () => {});

				container.append(choices);
			} else {
				choices.remove();
			}
		});
		container.append(selectedLine, choices);

		this._dom.container = container;
		this._dom.choices = choices;
		this._dom.selectedLine = selectedLine;
	}

	_selectItem(type: TMyMapsFoldersSelectedType, item?: IMyMapsFolderItem) {
		this._selectedType = type;
		this._isOpen = false;
		this._dom.choices.remove();
		this._dom.selectedLine.innerHTML = "";

		// aktivni polozka
		if (type === "folder") {
			this._selectedId = item.id;
			this._dom.selectedLine.append(createItemByType("folder", {
				title: item.title,
				img: item.img,
				isSelected: true,
			}));
		} else if (type === "places") {
			this._dom.selectedLine.append(createItemByType("places", {
				isSelected: true,
			}));
		} else if (type === "different-folder") {
			this._dom.selectedLine.append(createItemByType("different-folder", {
				isSelected: true,
			}));
		}

		this._opts.onSelect(type, item);
	}

	_createType(type: TMyMapsFoldersSelectedType, item?: IMyMapsFolderItem, noSelect = false) {
		const itemCover = node("li", { className: `mymaps-folders__choicesItem` });

		if (type === "folder") {
			const elem = createItemByType("folder", {
				title: item.title,
				img: item.img,
			});

			itemCover.append(elem);
		} else if (type === "move-folder") {
			const elem = createItemByType("move-folder", item, {
				isTrackLink: this._opts.isTrackLink,
			});

			itemCover.classList.add("move-folder");
			itemCover.append(elem);
		} else {
			const elem = type === "places"
				? createItemByType("places")
				: createItemByType("new-folder");

			itemCover.append(elem);
		}

		if (!noSelect) {
			itemCover.addEventListener("click", () => {
				this._selectItem(type, item);
			});
		}

		return itemCover;
	}
}
